// src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  Package,
  Layers,
  Factory,
  Boxes,
  AlertTriangle,
  ArrowRight,
  Shield,
  FileSpreadsheet,
  CheckCircle2,
  Calendar,
  AlertCircle,
  ExternalLink,
  Eye,
  X,
  Lock,
} from 'lucide-react';
import { DashboardData, SalesInvoice, SalesInvoiceItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { apiRequest } from '../lib/api';
import { formatDateOnly } from '../lib/date';
import { Badge } from '../components/ui/Badge';

export const Dashboard: React.FC = () => {
  const { user, can } = useAuth();
  const { settings } = useSettings();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  // Drilldown modal for viewing captured ERP invoice details
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [selectedItems, setSelectedItems] = useState<SalesInvoiceItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const openInvoiceDetails = async (inv: any) => {
    setSelectedInvoice(inv);
    setLoadingDetail(true);
    try {
      const res = await apiRequest<{ invoice: SalesInvoice; items: SalesInvoiceItem[] }>(`/api/invoices/${inv.id}`);
      if (res) {
        setSelectedItems(res.items || []);
      }
    } catch (err) {
      console.error('Failed to load invoice items', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadDashboard = async () => {
      try {
        const res = await apiRequest<DashboardData>('/api/dashboard');
        if (isMounted) setData(res);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadDashboard();
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-28 bg-slate-200 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-96 bg-slate-200 rounded-xl" />
          <div className="h-96 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  const canViewCost = Boolean(data?.canViewCost);
  const sales = data?.metrics?.sales;
  const inventory = data?.metrics?.inventory;
  const production = data?.metrics?.production;
  const deficitProducts = data?.deficitProducts || [];
  const recentInvoices = data?.recentInvoices || [];
  const recentBatches = data?.recentBatches || [];

  return (
    <div className="space-y-6">
      {/* Executive Welcome & Key Highlights Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-950 via-slate-900 to-[#0d5129]/80 p-6 sm:p-7 text-white shadow-xl border border-emerald-950">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-0.5 rounded-full bg-white/10 backdrop-blur-sm text-[11px] font-semibold tracking-wide uppercase text-emerald-200 border border-white/10">
              <Shield className="w-3 h-3 text-emerald-300" />
              <span>{user?.role?.name || 'Authorized Portal'}</span>
              <span className="text-slate-400">•</span>
              <span className="text-amber-300 font-mono">UAE Operations (AED)</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Executive Business Highlights
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
              Consolidated enterprise metrics across factory production output, warehouse stock valuation, and ERP sales profitability.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {can('invoices.view') && (
              <Link
                to="/sales-profitability"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs shadow transition"
              >
                <TrendingUp className="w-4 h-4" />
                Sales & GP Tracker
              </Link>
            )}
            {can('production.view') && (
              <Link
                to="/stock-inventory"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold text-xs backdrop-blur-sm border border-white/20 transition"
              >
                <Boxes className="w-4 h-4" />
                Stock & Valuation
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Critical Stock Deficit Banner (If any item is in negative balance) */}
      {deficitProducts.length > 0 && (
        <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-amber-900 shadow-sm animate-pulse">
          <div className="flex items-start sm:items-center gap-3">
            <div className="p-2 bg-amber-500 text-white rounded-lg shadow-sm">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold flex items-center gap-2 text-amber-950">
                Action Required: {deficitProducts.length} Product{deficitProducts.length > 1 ? 's' : ''} in Negative Physical Stock Deficit
              </h4>
              <p className="text-xs text-amber-800 mt-0.5">
                ERP billed products with 0 warehouse stock balance. Factory production batch must be logged to replenish inventory and auto-clear invoice flags.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {can('production.create') && (
              <Link
                to="/production-batches"
                className="text-xs font-bold px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition shadow-sm"
              >
                + Log Production Batch
              </Link>
            )}
            <Link
              to="/stock-inventory?stockFilter=deficit"
              className="text-xs font-semibold px-3 py-1.5 bg-white border border-amber-300 text-amber-900 hover:bg-amber-50 rounded-lg transition"
            >
              View Deficit Items →
            </Link>
          </div>
        </div>
      )}

      {/* 4 Key Executive Performance KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: Billed Sales Revenue */}
        <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-sm hover:shadow transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Total Billed Revenue
            </span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-slate-900 tracking-tight">
              AED {(sales?.total_sold_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-slate-500 font-medium">
                {sales?.total_invoices ?? 0} active invoices
              </span>
              {canViewCost && sales?.gp_margin_pct !== undefined && (
                <span className={`font-bold px-2 py-0.5 rounded-full text-[11px] ${
                  sales.gp_margin_pct >= 20 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {sales.gp_margin_pct.toFixed(1)}% GP Margin
                </span>
              )}
            </div>
          </div>
        </div>

        {/* KPI 2: Gross Profit Realization (or Billed Volume for non-cost users) */}
        <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-sm hover:shadow transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {canViewCost ? 'Total Gross Profit (GP)' : 'Invoices Billed'}
            </span>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {canViewCost ? (
              <>
                <div className="text-2xl font-black text-indigo-950 tracking-tight">
                  AED {(sales?.total_gp_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-medium">
                    Base Cost: AED {(sales?.total_base_cost ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  <Link to="/sales-profitability" className="text-indigo-600 font-semibold hover:underline">
                    Analyze →
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="text-2xl font-black text-indigo-950 tracking-tight">
                  {sales?.total_invoices ?? 0} Invoices
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Recorded in ERP sales tracking
                </div>
              </>
            )}
          </div>
        </div>

        {/* KPI 3: Warehouse Stock Valuation */}
        <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-sm hover:shadow transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {canViewCost ? 'Warehouse Stock Value' : 'Stock Physical Balance'}
            </span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Boxes className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            {canViewCost ? (
              <>
                <div className="text-2xl font-black text-blue-950 tracking-tight">
                  AED {(inventory?.total_base_cost_value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-medium">
                    {(inventory?.total_stock_units ?? 0).toLocaleString()} units on hand
                  </span>
                  <span className="text-blue-700 font-semibold">
                    {inventory?.in_stock_skus ?? 0} SKUs
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="text-2xl font-black text-blue-950 tracking-tight">
                  {(inventory?.total_stock_units ?? 0).toLocaleString()} Units
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>{inventory?.in_stock_skus ?? 0} In Stock SKUs</span>
                  <span>{inventory?.out_of_stock_skus ?? 0} Out of Stock</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* KPI 4: Factory Production Output */}
        <div className="bg-white p-5 rounded-xl border border-slate-200/90 shadow-sm hover:shadow transition">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Factory Production Output
            </span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
              <Factory className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-amber-950 tracking-tight">
              {(production?.total_units_produced ?? 0).toLocaleString()} Units
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-slate-500 font-medium">
                {production?.total_batches ?? 0} Batches logged
              </span>
              {canViewCost && (
                <span className="text-amber-700 font-semibold font-mono text-[11px]">
                  AED {(production?.total_production_cost ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Two-Column Operational Highlights: Recent Sales Invoices vs Recent Factory Batches */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Recent Invoices & Profitability */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-bold text-slate-900">Recent Sales Invoices (ERP)</h3>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Latest customer billings, billed revenue, and GP margin
              </p>
            </div>
            <Link
              to="/invoices"
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-200 transition"
            >
              Invoices Ledger <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="flex-1 overflow-x-auto">
            {recentInvoices.length > 0 ? (
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3">Invoice # / Date</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3 text-right">Billed (AED)</th>
                    {canViewCost && <th className="px-4 py-3 text-right">GP Margin</th>}
                    <th className="px-4 py-3 text-center">Stock Status</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentInvoices.map((inv) => {
                    const margin = inv.gp_margin_pct ?? 0;
                    return (
                      <tr
                        key={inv.id}
                        onClick={() => openInvoiceDetails(inv)}
                        className="hover:bg-slate-100/70 cursor-pointer transition group"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-bold text-slate-900 group-hover:text-emerald-700 transition">
                            {inv.invoice_no}
                          </div>
                          <div className="text-[10px] text-slate-400 font-mono">
                            {formatDateOnly(inv.invoice_date, settings.date_format)}
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-[140px] truncate" title={inv.customer_name}>
                          <span className="font-medium text-slate-800 block truncate">{inv.customer_name}</span>
                          <span className="text-[10px] text-slate-400">{inv.sales_person}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right font-mono font-bold text-slate-900">
                          {inv.total_sold_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        {canViewCost && (
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <span
                              className={`inline-block font-mono font-bold px-2 py-0.5 rounded text-[11px] ${
                                margin >= 20
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : margin >= 0
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-rose-50 text-rose-700 border border-rose-200'
                              }`}
                            >
                              {margin.toFixed(1)}%
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {inv.has_active_negative_stock ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">
                              <AlertTriangle className="w-2.5 h-2.5 text-amber-600" />
                              Deficit
                            </span>
                          ) : inv.has_negative_stock ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />
                              Restored
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
                              Normal
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => openInvoiceDetails(inv)}
                            className="p-1.5 hover:bg-emerald-50 text-slate-400 hover:text-emerald-700 rounded-lg transition"
                            title="View line items & GP breakdown"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-xs text-slate-500">
                No invoices recorded yet.
              </div>
            )}
          </div>
        </div>

        {/* Right: Recent Factory Production Runs */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Factory className="w-4 h-4 text-indigo-600" />
                <h3 className="text-sm font-bold text-slate-900">Recent Factory Production Batches</h3>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Output recorded by factory production team & stock increments
              </p>
            </div>
            {can('production.view') && (
              <Link
                to="/production-batches"
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-800 flex items-center gap-1 bg-indigo-50 px-2.5 py-1.5 rounded-lg border border-indigo-200 transition"
              >
                Production Log <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>

          <div className="flex-1 overflow-x-auto">
            {recentBatches.length > 0 ? (
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Product Name</th>
                    <th className="px-4 py-3 text-right">Batch Qty</th>
                    {canViewCost && <th className="px-4 py-3 text-right">Unit Cost (AED)</th>}
                    {canViewCost && <th className="px-4 py-3 text-right">Total (AED)</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentBatches.map((batch) => (
                    <tr key={batch.id} className="hover:bg-slate-50/80 transition">
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-[11px] text-slate-500">
                        {formatDateOnly(batch.batch_date, settings.date_format)}
                      </td>
                      <td className="px-4 py-3 max-w-[170px] truncate" title={batch.product_name}>
                        <span className="font-semibold text-slate-800 block truncate">{batch.product_name}</span>
                        {batch.notes && (
                          <span className="text-[10px] text-slate-400 block truncate">{batch.notes}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right font-mono font-bold text-slate-900">
                        {batch.qty.toLocaleString()} <span className="text-[10px] font-normal text-slate-500">{batch.unit}</span>
                      </td>
                      {canViewCost && (
                        <td className="px-4 py-3 whitespace-nowrap text-right font-mono text-slate-700">
                          {batch.unit_cost !== undefined ? batch.unit_cost.toFixed(2) : '-'}
                        </td>
                      )}
                      {canViewCost && (
                        <td className="px-4 py-3 whitespace-nowrap text-right font-mono font-bold text-indigo-900">
                          {batch.total_cost !== undefined ? batch.total_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-xs text-slate-500">
                No production batches logged yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Navigation Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link
          to="/stock-inventory"
          className="p-4 bg-white rounded-xl border border-slate-200/90 hover:border-emerald-300 hover:bg-emerald-50/20 transition shadow-sm flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-lg group-hover:bg-emerald-100 transition">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 group-hover:text-emerald-800">
                Stock Details & Valuation
              </h4>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Live warehouse units, AED valuation, and filters
              </p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition" />
        </Link>

        <Link
          to="/production-batches"
          className="p-4 bg-white rounded-xl border border-slate-200/90 hover:border-indigo-300 hover:bg-indigo-50/20 transition shadow-sm flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-lg group-hover:bg-indigo-100 transition">
              <Factory className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 group-hover:text-indigo-800">
                Production Batch Ledger
              </h4>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Factory logs, batch costing, and stock receipts
              </p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition" />
        </Link>

        <Link
          to="/sales-profitability"
          className="p-4 bg-white rounded-xl border border-slate-200/90 hover:border-blue-300 hover:bg-blue-50/20 transition shadow-sm flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 text-blue-700 rounded-lg group-hover:bg-blue-100 transition">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-slate-900 group-hover:text-blue-800">
                Sales & Profitability Analytics
              </h4>
              <p className="text-[11px] text-slate-500 mt-0.5">
                ERP invoice imports, GP tracker, and monthly trends
              </p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition" />
        </Link>
      </div>

      {/* Invoice Detail Drilldown Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 bg-slate-950 text-white flex items-center justify-between border-b border-slate-800">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                    ERP Captured Invoice
                  </span>
                  <span className="text-[11px] bg-white/10 text-slate-300 px-2 py-0.5 rounded font-mono">
                    {formatDateOnly(selectedInvoice.invoice_date, settings.date_format)}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                    selectedInvoice.status === 'paid' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
                  }`}>
                    {selectedInvoice.status || 'Active'}
                  </span>
                </div>
                <h3 className="text-xl font-black font-mono mt-1 text-white flex items-center gap-2">
                  {selectedInvoice.invoice_no}
                  <span className="text-xs font-normal text-slate-400">({selectedInvoice.source || 'ERP Ingested'})</span>
                </h3>
                <p className="text-xs text-slate-300 mt-0.5">
                  Customer: <strong className="text-white">{selectedInvoice.customer_name}</strong> • Sales Rep: <strong className="text-emerald-300">{selectedInvoice.sales_person}</strong>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedInvoice(null)}
                className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {/* Active Negative Stock Alert */}
              {Boolean(selectedInvoice.has_active_negative_stock) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center gap-3 text-xs text-amber-900">
                  <div className="p-2 bg-amber-500 text-white rounded-lg flex-shrink-0">
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="font-bold">Generated with Negative Physical Stock:</strong> This invoice was billed in the ERP when warehouse physical stock was 0 or insufficient. A factory production batch must be logged to replenish inventory and auto-clear this flag.
                  </div>
                </div>
              )}

              {/* KPI Summary Strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>
                  <span className="text-[11px] text-slate-500 font-semibold block uppercase">Total Billed</span>
                  <span className="text-lg font-bold text-slate-900 font-mono">
                    AED {(selectedInvoice.total_sold_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                {canViewCost && (
                  <div>
                    <span className="text-[11px] text-slate-500 font-semibold block uppercase">Base Cost</span>
                    <span className="text-lg font-bold text-slate-700 font-mono">
                      AED {(selectedInvoice.total_base_cost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {canViewCost && (
                  <div>
                    <span className="text-[11px] text-slate-500 font-semibold block uppercase">Gross Profit</span>
                    <span className="text-lg font-bold text-emerald-700 font-mono">
                      AED {(selectedInvoice.total_gp_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {canViewCost && (
                  <div>
                    <span className="text-[11px] text-slate-500 font-semibold block uppercase">GP Margin</span>
                    <span className={`inline-block font-mono font-bold px-2 py-0.5 rounded text-xs mt-1 ${
                      (selectedInvoice.gp_margin_pct ?? 0) >= 20 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {(selectedInvoice.gp_margin_pct ?? 0).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>

              {/* Line Items Table */}
              <div>
                <h4 className="font-bold text-slate-900 text-sm mb-3">Line Items Captured from ERP</h4>
                {loadingDetail ? (
                  <div className="p-8 text-center text-xs text-slate-400">Loading line item breakdown...</div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100/80 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                        <tr>
                          <th className="p-3">ERP Item Description</th>
                          <th className="p-3 text-right">Qty</th>
                          <th className="p-3 text-right">Sold Rate (AED)</th>
                          <th className="p-3 text-right">Total Billed</th>
                          {canViewCost && <th className="p-3 text-right">Base Cost</th>}
                          {canViewCost && <th className="p-3 text-right">GP (AED)</th>}
                          {canViewCost && <th className="p-3 text-right">Margin %</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedItems.length > 0 ? (
                          selectedItems.map((item) => (
                            <tr key={item.id} className="hover:bg-slate-50 transition">
                              <td className="p-3 font-medium text-slate-800">
                                <div>{item.item_name}</div>
                                {item.catalog_product_name && item.catalog_product_name !== item.item_name && (
                                  <div className="text-[10px] text-emerald-600">Matched: {item.catalog_product_name}</div>
                                )}
                              </td>
                              <td className="p-3 text-right font-mono font-semibold text-slate-900">
                                {item.qty.toLocaleString()} <span className="text-[10px] text-slate-400 font-normal">{item.uom}</span>
                              </td>
                              <td className="p-3 text-right font-mono text-slate-700">
                                {item.sold_rate.toFixed(2)}
                              </td>
                              <td className="p-3 text-right font-mono font-bold text-slate-900">
                                {item.sold_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              {canViewCost && (
                                <td className="p-3 text-right font-mono text-slate-600">
                                  {item.total_base_cost !== undefined ? item.total_base_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                </td>
                              )}
                              {canViewCost && (
                                <td className="p-3 text-right font-mono font-bold text-emerald-700">
                                  {item.gp_amount !== undefined ? item.gp_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                                </td>
                              )}
                              {canViewCost && (
                                <td className="p-3 text-right">
                                  <span className={`inline-block font-mono font-bold px-1.5 py-0.5 rounded text-[11px] ${
                                    (item.gp_margin_pct ?? 0) >= 20 ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'
                                  }`}>
                                    {item.gp_margin_pct !== undefined ? `${item.gp_margin_pct.toFixed(1)}%` : '-'}
                                  </span>
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="p-6 text-center text-slate-400 text-xs">
                              No line items found for this invoice.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
              <span className="font-mono">ERP Invoice ID #{selectedInvoice.id}</span>
              <button
                type="button"
                onClick={() => setSelectedInvoice(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

